import React, { useMemo } from 'react';
import { Button } from '@web/components/ui/button';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Show the page size selector (default: true) */
  showPageSizeSelector?: boolean;
  /** Show the item range text (default: true) */
  showItemRange?: boolean;
  /** For smaller screens, limit visible page buttons */
  compactMode?: boolean;
};

type PageItem = number | 'ellipsis';

export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  showPageSizeSelector = true,
  showItemRange = true,
  compactMode = false,
}: PaginationProps) {
  // Calculate start and end item numbers for the current page
  const startItem = totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  /**
   * Generates an array of page items (numbers and ellipsis) for pagination.
   * If total pages is less than or equal to maxVisiblePages, all pages are returned.
   * Otherwise, the first, last, and a window around the current page are shown.
   */
  const getPaginationItems = (
    current: number,
    total: number,
    maxVisiblePages = compactMode ? 3 : 5,
  ): PageItem[] => {
    if (total <= 1) {
      return [1];
    }

    if (total <= maxVisiblePages) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: PageItem[] = [];

    // Always include first page
    pages.push(1);

    // Calculate window around current page
    let windowStart = Math.max(
      2,
      current - Math.floor(maxVisiblePages / 2) + 1,
    );
    let windowEnd = windowStart + maxVisiblePages - 3; // -3 because we always show first and last page

    // Adjust if window extends beyond the last page
    if (windowEnd >= total) {
      windowEnd = total - 1;
      windowStart = Math.max(2, windowEnd - maxVisiblePages + 3);
    }

    // Add ellipsis after first page if needed
    if (windowStart > 2) {
      pages.push('ellipsis');
    }

    // Add pages in the window
    for (let i = windowStart; i <= windowEnd; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (windowEnd < total - 1) {
      pages.push('ellipsis');
    }

    // Always include last page if not already included
    if (total > 1) {
      pages.push(total);
    }

    return pages;
  };

  const paginationItems = useMemo(
    () => getPaginationItems(currentPage, totalPages),
    [currentPage, totalPages, compactMode],
  );

  // Return null if there's nothing to paginate
  if (totalPages <= 1 && !showItemRange && !showPageSizeSelector) {
    return null;
  }

  return (
    <div className="mt-4 flex flex-col items-center justify-between gap-3 rounded-md bg-white px-2 py-3 shadow-sm md:flex-row md:gap-0 md:px-4">
      {/* Display of current items range */}
      {showItemRange && totalItems > 0 && (
        <div className="text-xs text-gray-600 md:text-sm">
          <span className="hidden md:inline">Showing </span>
          <span className="font-medium">
            {startItem}–{endItem}
          </span>
          <span className="hidden md:inline"> of </span>
          <span className="inline md:hidden"> / </span>
          <span className="font-medium">{totalItems}</span>
        </div>
      )}

      {/* Pagination controls - now more minimal */}
      <div className="flex items-center gap-1 md:gap-2">
        {/* First page button (only show when not in first few pages) */}
        {!compactMode && currentPage > 3 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-500"
            onClick={() => {
              onPageChange(1);
            }}
            disabled={currentPage === 1}
            aria-label="First Page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
        )}

        {/* Previous page button */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-gray-500"
          onClick={() => {
            onPageChange(currentPage - 1);
          }}
          disabled={currentPage === 1}
          aria-label="Previous Page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Page numbers - mobile optimized */}
        <div className="flex items-center">
          {paginationItems.map((item, index) =>
            item === 'ellipsis' ? (
              <span
                key={`ellipsis-${index}`}
                className="flex h-8 w-8 items-center justify-center text-xs text-gray-500"
              >
                ⋯
              </span>
            ) : (
              <Button
                key={item}
                variant={currentPage === item ? 'default' : 'ghost'}
                size="sm"
                className={`h-8 w-8 p-0 ${
                  currentPage === item
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => onPageChange(item)}
                aria-label={`Page ${item}`}
                aria-current={currentPage === item ? 'page' : undefined}
              >
                {item}
              </Button>
            ),
          )}
        </div>

        {/* Next page button */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-gray-500"
          onClick={() => {
            onPageChange(currentPage + 1);
          }}
          disabled={currentPage === totalPages || totalPages === 0}
          aria-label="Next Page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Last page button (only show when not in last few pages) */}
        {!compactMode && totalPages > 3 && currentPage < totalPages - 2 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-500"
            onClick={() => {
              onPageChange(totalPages);
            }}
            disabled={currentPage === totalPages}
            aria-label="Last Page"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Page size selector - more minimal */}
      {showPageSizeSelector && (
        <div className="flex items-center md:ml-3">
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              onPageSizeChange(Number(value));
            }}
            aria-label="Items per page"
          >
            <SelectTrigger
              className="h-8 border-none bg-transparent px-2 text-xs shadow-none hover:bg-gray-100 md:h-9 md:text-sm"
              aria-label="Items per page"
            >
              <span className="hidden text-gray-500 md:inline">Show: </span>
              <SelectValue className="pl-0">{pageSize}</SelectValue>
            </SelectTrigger>
            <SelectContent side="top" align="end">
              {pageSizeOptions.map((option) => (
                <SelectItem
                  key={option}
                  value={option.toString()}
                  className="text-sm"
                >
                  {option} per page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
